import React, {
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';

const InfiniteScrollList = forwardRef(
  (
    {
      items,
      renderItem,
      onTrigger,
      style,
      className = '',
      isLoading = false,
      hasMore = true,
      threshold = 0.75,
    },
    ref
  ) => {
    const loaderRef = useRef(null);
    const scrollRef = useRef(null);
    const observerRef = useRef(null);

    const handleObserver = useCallback(
      ([entry]) => {
        if (entry.isIntersecting && !isLoading && hasMore) {
          onTrigger();
        }
      },
      [onTrigger, isLoading, hasMore]
    );

    useEffect(() => {
      if (!loaderRef.current || !scrollRef.current || !hasMore || isLoading) return;

      observerRef.current?.disconnect();
      observerRef.current = new IntersectionObserver(handleObserver, {
        root: scrollRef.current,
        threshold,
      });
      observerRef.current.observe(loaderRef.current);

      return () => observerRef.current?.disconnect();
    }, [handleObserver, hasMore, isLoading, threshold]);

    // ⚡️ 외부에서 scrollToTop()을 사용할 수 있도록 expose
    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      },
    }));

    return (
      <div
        ref={scrollRef}
        className={`${className}`.trim()}
        style={{ overflowY: 'auto', height: '100%', ...style }}
      >
        {items.map((item) => (
          <div key={item.id}>{renderItem(item)}</div>
        ))}
        {hasMore && <div ref={loaderRef} style={{ height: '1px' }} />}
      </div>
    );
  }
);

export default InfiniteScrollList;
